import { env } from '@/env/client';
import {
  AdminAuthState,
  ClientSession,
  getUserRefreshTokenPath,
  RefreshToken,
  Tenant,
  UserAuthState,
  WebAuth,
} from '@payleo/auth/web';
import { getOrganizationStatus } from '@payleo/ewa/manage';
import Arusha from '@payleo/platform/arusha';
import { getCompanyStatus } from '@payleo/platform/payments';
import {
  redirect,
  RouterContextProvider,
  type DataStrategyResult,
  type MiddlewareFunction,
} from 'react-router';

import Cookies from 'universal-cookie';
import { arushaClient, ewaClient, getIdentityAuth, paymentsClient } from '.';
import {
  CompanyContext,
  UserContext,
  type CompanyContextValue,
  type UserContextValue,
} from './appcontext';

const COOKIE_SESSION = 'Payleo-Manage-Auth-Session';
const COOKIE_REFRESH = 'Payleo-Manage-Auth-Refresh';
const COOKIE_ALLOWED_DOMAIN = '.payleo.tz';

export function createLocalStoragePersistance(auth: WebAuth) {
  const isLocal = env.VITE_APP_ENV === 'development';
  const cookies = new Cookies();

  return {
    saveState(state: {
      session?: ClientSession | null;
      refreshToken: RefreshToken;
      refreshTokenPath: string;
    }) {
      if (state.session) {
        this.saveSessionState(state.session);
      }

      const seconds = state.refreshToken.getSecondsLeft();
      // saving refresh token
      cookies.set(
        COOKIE_REFRESH,
        `${state.session?.type ?? 'user'}:${state.refreshToken.toString()}:${
          state.refreshTokenPath
        }`,
        {
          sameSite: 'lax',
          httpOnly: false,
          secure: !isLocal,
          // should have the age of the refresh token expiry date
          maxAge: seconds,
          expires: state.refreshToken.expiresAt,
          path: '/',
          domain: isLocal ? undefined : COOKIE_ALLOWED_DOMAIN,
        }
      );
    },
    saveSessionState(session: ClientSession) {
      cookies.set(COOKIE_SESSION, `${session.type}:${session.token}`, {
        sameSite: 'lax',
        httpOnly: false,
        secure: !isLocal,
        // should have the age of the refresh token expiry date
        maxAge: session.expiresIn,
        expires: session.expiresAt,
        path: '/',
        domain: isLocal ? undefined : COOKIE_ALLOWED_DOMAIN,
      });
    },
    getSessionState(): ClientSession | null {
      const ustate: string | null = cookies.get(COOKIE_SESSION);
      if (!ustate) return null;
      if (ustate.startsWith('admin:')) {
        return AdminAuthState.fromToken(ustate.substring('admin:'.length));
      }

      if (ustate.startsWith('user:')) {
        return UserAuthState.fromToken(ustate.substring('user:'.length));
      }

      console.warn('unknown or unsupported session state');
      return null;
    },
    syncAuthState() {
      // pull auth state from the cookies

      let session:
        { value: AdminAuthState; utype: 'admin' } | { value: UserAuthState; utype: 'user' } | null =
        null;
      let refreshTkn: { value: RefreshToken; utype: 'admin' | 'user'; tokenPath: string } | null =
        null;
      /**
       * Should contain user information as `user:<session-token>`
       * Or as `admin:<session-token>`
       */
      const ustate: string | null = cookies.get(COOKIE_SESSION);

      if (ustate) {
        // set the state
        if (ustate.startsWith('admin:')) {
          session = {
            utype: 'admin',
            value: AdminAuthState.fromToken(ustate.substring('admin:'.length)),
          };
        }

        if (ustate.startsWith('user:')) {
          session = {
            utype: 'user',
            value: UserAuthState.fromToken(ustate.substring('user:'.length)),
          };
        }
      }

      const rstate: string | null = cookies.get(COOKIE_REFRESH);

      if (typeof rstate === 'string' && rstate.trim() !== '') {
        const parts = rstate.split(':');
        if (parts.length !== 3) {
          // it's probably using an older version
          this.deleteState();
          return;
        }

        const [type, token, path] = parts;
        refreshTkn = {
          utype: type as 'admin' | 'user',
          value: RefreshToken.fromRefreshJWT(token),
          tokenPath: path,
        };
      }

      // order. if session is present,
      // and the refresh token is not of the same utype, invalidate the refresh token.
      // if session is missing, use refresh token if present

      if (session) {
        if (refreshTkn && refreshTkn.utype !== session.utype) {
          cookies.remove(COOKIE_REFRESH, { path: '/' });
        }

        auth.saveAuthState({
          session: session.value,
          refreshTokenPath:
            session.value.type === 'admin'
              ? AdminAuthState.getRefreshTokenPath()
              : getUserRefreshTokenPath(session.value),
          refreshToken: refreshTkn?.value ?? null,
        });
      } else {
        if (refreshTkn) {
          auth.saveAuthState({
            session: null,
            refreshToken: refreshTkn.value ?? null,
            refreshTokenPath: refreshTkn.tokenPath,
          });
        }
      }

      return auth.onAuthStateChange((state) => {
        if (state) {
          if (state.refreshToken && state.refreshTokenPath) {
            this.saveState({
              session: state.session,
              refreshToken: state.refreshToken,
              refreshTokenPath: state.refreshTokenPath,
            });
            return;
          }
        }

        // assume null and remove
        this.deleteState();
        // handler(state);
      });
    },
    deleteState() {
      // we assume the state is removed.
      // i.e. the user has logged out
      cookies.remove(COOKIE_SESSION, { path: '/' });
      cookies.remove(COOKIE_REFRESH, { path: '/' });
    },
  };
}

export const cookieAuth = createLocalStoragePersistance(getIdentityAuth());

// ---------------------------------------------------------------------------
// Router context
// ---------------------------------------------------------------------------

type ClientMiddleware = MiddlewareFunction<Record<string, DataStrategyResult>>;
type UserProfile = UserContextValue['profile'];

/** Profiles of signed-in users, keyed by session id */
const profileCache = new Map<string, UserProfile>();
/** Resolved companies, keyed by `<session-id>:<domain>` */
const companyCache = new Map<
  string,
  Promise<{ company: CompanyContextValue; profile: UserProfile }>
>();

/**
 * Drops the cached company resolution for `domain` (e.g. after subscribing to a
 * service), so the next navigation / revalidation re-resolves `enabledServices`.
 */
export function invalidateCompany(domain: string) {
  for (const key of companyCache.keys()) {
    if (key.endsWith(`:${domain}`)) companyCache.delete(key);
  }
}

getIdentityAuth().onAuthStateChange((state) => {
  if (state === null) {
    // logged out. drop anything tied to the previous user
    profileCache.clear();
    companyCache.clear();
  }
});

function getCurrentSession(auth: WebAuth) {
  return (auth.currentUser?.session ?? null) as AdminAuthState | UserAuthState | null;
}

function toUserContext(
  session: AdminAuthState | UserAuthState,
  profile: UserProfile = {}
): UserContextValue {
  if (session.type === 'admin') {
    return {
      id: session.id,
      type: 'admin',
      role: session.role,
      scopes: session.scopes,
      displayName: profile.name,
      profile,
    };
  }

  return {
    id: session.id,
    type: 'user',
    role: session.tenant?.role ?? null,
    scopes: session.tenant?.scopes ?? [],
    displayName: profile.name,
    profile,
  };
}

/**
 * Passed to `<HydratedRouter getContext>`. Called on every navigation/fetch,
 * seeding the router context with the current auth state.
 */
export function getRouterContext() {
  const context = new RouterContextProvider();
  const session = getCurrentSession(getIdentityAuth());
  context.set(UserContext, session ? toUserContext(session, profileCache.get(session.id)) : null);
  return context;
}

/**
 * Redirects to `/login` when there's no signed-in user.
 */
export const requireUserMiddleware: ClientMiddleware = ({ context, url }) => {
  if (!context.get(UserContext)) {
    const search = new URLSearchParams({ continue: url.pathname + url.search });
    throw redirect(`/login?${search.toString()}`);
  }
};

async function resolveCompany(domain: string) {
  const client = arushaClient();
  const [company, profile] = await Promise.all([
    Arusha.queryCompany(client, { domain }),
    Arusha.getUserProfile(client, ['name', 'email']),
  ]);

  // NOTE: these are checked one after the other, as when one request fails,
  // for some reason the other is `auto-aborted`.
  const enabledServices: string[] = [];
  try {
    await getCompanyStatus(paymentsClient(), company.id);
    enabledServices.push('disbursements');
  } catch (err) {
    console.error('error http.call for payments', err);
  }

  try {
    await getOrganizationStatus(ewaClient(), company.id);
    enabledServices.push('salary_loans');
  } catch (err) {
    console.error('err http.call for ewa', err);
  }

  return {
    company: {
      id: company.id,
      publicId: company.public_id,
      domain,
      displayName: company.display_name,
      enabledServices,
    },
    profile: {
      name: profile.getValue<string>('name'),
      email: profile.getValue<string>('email'),
    },
  };
}

/**
 * Resolves the company from the `:domain` param into `CompanyContext`, and
 * makes sure the `UserContext` reflects the user's access within that company.
 *
 * Must run after `requireUserMiddleware`.
 */
export const companyMiddleware: ClientMiddleware = async ({ params, context }) => {
  const domain = params.domain;
  if (!domain) {
    throw new Response('Missing company domain', { status: 404 });
  }

  const auth = getIdentityAuth();
  let session = getCurrentSession(auth);
  if (!session) {
    throw redirect('/login');
  }

  const key = `${session.id}:${domain}`;
  let resolving = companyCache.get(key);
  if (!resolving) {
    resolving = resolveCompany(domain);
    companyCache.set(key, resolving);
    // don't hold on to failures, so that the next navigation retries
    resolving.catch(() => companyCache.delete(key));
  }
  const { company, profile } = await resolving;

  // users need a tenant session scoped to this company to get their role/scopes
  if (session.type === 'user' && session.tenant?.domain !== domain) {
    await Tenant.requestToken(auth, session.token, domain);
    session = getCurrentSession(auth) ?? session;
  }

  profileCache.set(session.id, profile);
  context.set(UserContext, toUserContext(session, profile));
  context.set(CompanyContext, company);
};
